import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import DangerCircleIcon from '@/images/DangerCircle.svg';
import { validatePublicInsightsShare } from '@/services/api/did-api/insights-api';
import theme from '@/theme';
import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { ConversationsTable } from './components/ConversationsTable';
import { PublicInsightsHeader } from './components/Header/PublicInsightsHeader';
import { SummaryTable } from './components/SummaryTable';
import { TopicsTable } from './components/TopicsTable';
import { AgentInsightsContextProvider } from './contex/AgentInsightsContext';
import { useAgentInsightsContext } from './contex/useAgentInsightsContext';

const PageContainer = styled.div`
    background-color: ${({ theme }) => theme.colors.black[50]};
    width: 100%;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
`;

const ContentWrapper = styled.div`
    padding: 32px;
    width: 100%;
    height: 100%;
    overflow-x: hidden;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 1400px;
    margin: 0 auto;
    width: 100%;
`;

const LoadingPage = styled.div`
    background-color: ${({ theme }) => theme.colors.black[50]};
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
`;

const LoadingText = styled.div`
    color: ${({ theme }) => theme.colors.black[700]};
    font-size: 16px;
    font-weight: 500;
    margin-top: 8px;
`;

const ErrorContainer = styled.div`
    width: 100%;
    max-width: 550px;
    margin: 0 auto;
    padding: 48px 32px;
`;

const ErrorCard = styled.div`
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    padding: 40px 32px;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    text-align: center;
`;

const ErrorIconWrapper = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: #fef2f2;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const ErrorTitle = styled.h2`
    color: ${({ theme }) => theme.colors.secondary.main};
    font-size: 20px;
    font-weight: 600;
    margin: 0;
    line-height: 28px;
`;

const ErrorMessage = styled.p`
    color: ${({ theme }) => theme.colors.black[700]};
    font-size: 14px;
    font-weight: 400;
    margin: 0;
    line-height: 20px;
`;

const PublicAgentInsightsPage = () => {
    const { isConversationView } = useAgentInsightsContext();

    return (
        <PageContainer>
            <Helmet>
                <title>Agent Insights - D-ID</title>
                <meta name="robots" content="noindex" />
            </Helmet>
            <ContentWrapper>
                <Container>
                    <PublicInsightsHeader />

                    {!isConversationView && (
                        <>
                            <SummaryTable />
                            <TopicsTable />
                        </>
                    )}
                    {isConversationView && <ConversationsTable />}
                </Container>
            </ContentWrapper>
        </PageContainer>
    );
};

const PublicAgentInsightsPageWrapper = () => {
    const [searchParams] = useSearchParams();
    const shareId = searchParams.get('id');
    const { isLoading: isValidating, isError } = useQuery(
        ['validatePublicInsightsShare', shareId],
        () => validatePublicInsightsShare(shareId!),
        {
            enabled: !!shareId,
            retry: false,
        }
    );

    if (isValidating) {
        return (
            <LoadingPage>
                <CircularProgress size={48} sx={{ color: theme.colors.primary.main }} />
                <LoadingText>Loading insights...</LoadingText>
            </LoadingPage>
        );
    }

    if (isError || !shareId) {
        return (
            <LoadingPage>
                <Helmet>
                    <title>Invalid Link - D-ID</title>
                    <meta name="robots" content="noindex" />
                </Helmet>
                <ErrorContainer>
                    <ErrorCard>
                        <ErrorIconWrapper>
                            <MaskedIcon src={DangerCircleIcon} size="32px" color={theme.colors.error.main} />
                        </ErrorIconWrapper>
                        <div>
                            <ErrorTitle>Invalid share link</ErrorTitle>
                            <ErrorMessage>
                                The insights link you're trying to access is invalid or has expired. Please contact the person who shared
                                this link with you.
                            </ErrorMessage>
                        </div>
                    </ErrorCard>
                </ErrorContainer>
            </LoadingPage>
        );
    }

    return (
        <AgentInsightsContextProvider shareId={shareId} isPublic>
            <PublicAgentInsightsPage />
        </AgentInsightsContextProvider>
    );
};

export { PublicAgentInsightsPageWrapper as PublicAgentInsightsPage };
